import { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useCoordinationClient } from '@confluence/engagement-provider';
import type { DialogsStateContainer } from '@confluence/dialogs';
import { Attribution } from '@confluence/error-boundary';
import { getMonitoringClient } from '@confluence/monitoring';

import { startMessage, stopMessage } from '../utils/manage-engagement-platform-message';
import { LazyRecommendTrialToFree } from '..';
import { RECOMMEND_TO_FREE_ANALYTICS_SOURCE } from '../package-constants';

import { useFireExperimentExposureAndAnalytics } from './useFireExperimentExposure';
import { useExperimentEnrollment } from './useExperimentEnrollment';

export const useRecommendTrialToFreeExperiment = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const engagementPlatformCoordinationClient = useCoordinationClient();
	const { isEnrolled, cohort } = useExperimentEnrollment();
	const { fireExposure } = useFireExperimentExposureAndAnalytics();

	const triggerRecommendTrialToFreeExperimentCheck = useCallback(
		async ({
			isUpdatingPage,
			dialogs,
		}: {
			isUpdatingPage: boolean;
			dialogs?: DialogsStateContainer;
		}) => {
			let isExperimentActivated = false;

			try {
				if (isUpdatingPage && isEnrolled) {
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'qualified',
							actionSubject: 'confluenceRecommendToFreeExperiment',
							source: RECOMMEND_TO_FREE_ANALYTICS_SOURCE,
						},
					}).fire();

					const isMessageActive = await startMessage(engagementPlatformCoordinationClient);

					if (isMessageActive) {
						fireExposure();

						if (cohort === 'test' && dialogs !== undefined) {
							dialogs?.showModal(LazyRecommendTrialToFree, {
								stopMessage: () => stopMessage(engagementPlatformCoordinationClient),
							});
							isExperimentActivated = true;
						} else {
							void stopMessage(engagementPlatformCoordinationClient);
						}
					}
				}
			} catch (err) {
				getMonitoringClient().submitError(err, {
					attribution: Attribution.GROWTH_LUNA,
				});
			}

			return { isExperimentActivated };
		},
		[isEnrolled, cohort, engagementPlatformCoordinationClient, fireExposure, createAnalyticsEvent],
	);

	return { triggerRecommendTrialToFreeExperimentCheck };
};
